/** Custom wrapper to use `setTimeout()` with `await`. */
export async function sleep(msDelay: number) {
  return new Promise(resolve => setTimeout(resolve, msDelay));
}

/** Alternative to sleep(), but uses setInterval() instead of setTimeout(). (Still triggers only once!)
 * The only reason for this is because protractor waits for all setTimeout()s to complete before doing the next action.
 * With `sleepByInterval()` one can spawn async tasks (e.g. auto-dismiss popup toast notification) and protractor will
 * not be blocked on that.
 */
export async function sleepByInterval(msDelay: number) {
  return new Promise<void>(resolve => {
    let intervalId = setInterval(() => {
      clearInterval(intervalId);
      resolve();
    }, msDelay);
  });
}

/**
 * Note that older versions of TypeScript (before 2.8) cannot override native objects properly, so
 * `foo instanceof TimeoutError` is never true.
 * Use `foo.name==='TimeoutError'` instead!
 *
 * *Also note*: Protractor has its own errors that are also called "TimeoutError", so checking by name
 * will not work for those cases!
 */
export class TimeoutError extends Error {
  timeout: number;
  constructor(timeout: number) {
    super(`timeout after ${timeout}ms`);
    this.timeout = timeout;
    this.name = 'TimeoutError';
  }
}

/**
 * Reject a promise if it runs for longer then some time limit.
 * If timeout is 0, this never times out - i.e. just passes the promise through.
 */
export function timeout<T>(timeout_ms: number, promise: Promise<T>): Promise<T> {
  if (timeout_ms === 0) {
    return promise;
  }
  let error = new TimeoutError(timeout_ms);

  // firefox doesn't support `Error.captureStackTrace()`, but we can call it on both Chromium and Node
  let captureStackTrace = Error['captureStackTrace'];
  if (typeof captureStackTrace === 'function') {
    captureStackTrace(error, timeout);
  }

  return new Promise<T>((resolve, reject) => {
    const timerId = setTimeout(() => {
      reject(error);
    }, timeout_ms);
    promise
      .then(response => {
        clearTimeout(timerId);
        resolve(response);
      })
      .catch(rejection => {
        clearTimeout(timerId);
        reject(rejection);
      });
  });
}

export class DeferredPromise<T> extends Promise<T> {
  public resolve: (val: T) => void;
  public reject: (reason: any) => void;

  static defer<T>(): DeferredPromise<T> {
    let res, rej;

    let promise = new DeferredPromise<T>((resolve, reject) => {
      res = resolve;
      rej = reject;
    });

    promise.resolve = res;
    promise.reject = rej;

    return promise;
  }
}

type AsyncFunction<R> = (...args: any[]) => Promise<R>;

/// Wrap an asynchronous function so that it can be called only once at a time. I.e. all further calls will return the same promise.
/// If `opts.forever` is true, then the wrapper will always return the same value until the end of app lifetime.
export function generateAsyncSingletonFunction<R>(
  fn: AsyncFunction<R>, opts?: { forever?: boolean },
): AsyncFunction<R> {
  let activePromise: Promise<R> | null = null;
  let forever = opts && opts.forever;

  const executeAndReset = async (...args: any[]): Promise<R> => {
    try {
      let result = await fn(...args);
      return result;

    } finally {
      if (!forever) {
        activePromise = null;
      }
    }
  };

  const wrappedFn = async (...args: any[]): Promise<R> => {
    if (activePromise === null) {
      activePromise = executeAndReset(...args);
    }
    return activePromise;
  };

  return wrappedFn;
}

/**
Wrap a pair of init/shutdown functions so that init can be called multiple times, but only activate once.
When the shutdown function is called, it resets the state, and init will be activated again on next call.

A FAILED init is not remembered. The rejection still reaches everybody who is already waiting on
that attempt, but the stored attempt is dropped, so the next caller starts a real new one. Caching
the rejection instead made a single transient failure permanent for the life of the page: every
later caller got the original error back without `initFn` ever running again, and only a reload
could clear it. `generateAsyncSingletonFunction` above has always behaved this way; the two now
agree about what a failure means.
*/
export function generateAsyncInitShutdownPair(initFn: () => Promise<void>, shutdownFn: () => Promise<void>): { init: () => Promise<void>, shutdown: () => Promise<void> } {
  let activePromise: Promise<void> | null = null;

  let wrappedInit = async () => {
    if (activePromise === null) {
      // the identity check matters: by the time a failure lands, a shutdown may have cleared the
      // slot or a newer attempt may own it, and this older one must not reach in and undo that
      const attempt: Promise<void> = initFn().catch((err) => {
        if (activePromise === attempt) {
          activePromise = null;
        }
        throw err;
      });
      activePromise = attempt;
    }
    return activePromise;
  }
  let wrappedShutdown = async () => {
    const awaited = activePromise;
    if (awaited !== null) {
      // wait for an in-flight init to settle, so teardown never runs against half-built state.
      // A FAILED init must not stop the teardown though: the partial state it left behind is
      // exactly what needs cleaning up. The error is not being swallowed — it is delivered to
      // whoever called init(), and shutdown is a different caller with a different job.
      await awaited.catch(() => { });
      // clear only the attempt we actually waited on. While this await was parked, the attempt we
      // waited on can have failed and cleared its own slot, and a RETRY can already own the slot —
      // that happens whenever the shutdown was requested at least one tick after the init, which
      // is the ordinary shape (a boot opens the database, a sign-out arrives much later). Nulling
      // the slot then would discard the retry, and the next init() would find it empty and start a
      // SECOND build running beside the first.
      //
      // What this does NOT do, so nobody expects it to: `shutdownFn` can still run while that
      // retry is in flight. The shutdown only ever promised to wait for the attempt it captured,
      // and the retry did not exist when it was requested.
      if (activePromise === awaited) {
        activePromise = null;
      }
    }
    return shutdownFn();
  }

  return { init: wrappedInit, shutdown: wrappedShutdown };
}

/**
 * Creates a wrapper for async functions that prevents race conditions.
 * Only the most recent call's results will be applied via the callback.
 * 
 * This is useful when multiple async operations can be triggered in quick succession
 * (e.g., from rapid user input or Angular change detection), but you only want the
 * results from the most recent call to be applied.
 * 
 * @param asyncFn - The async function to wrap
 * @param applyResult - Callback to apply the result (only called for latest)
 * @returns Wrapped function that handles race conditions
 * 
 * @example
 * const safeGenerate = createAsyncRaceHandler(
 *   async (data) => computeExpensiveResult(data),
 *   (result) => this.state = result
 * );
 * // Multiple rapid calls - only latest result applied
 * safeGenerate(data1); 
 * safeGenerate(data2); // This result will be applied
 */
export function createAsyncRaceHandler<TArgs extends any[], TResult>(
  asyncFn: (...args: TArgs) => Promise<TResult>,
  applyResult: (result: TResult) => void
): (...args: TArgs) => Promise<void> {
  let latestCallId = 0;

  return async (...args: TArgs): Promise<void> => {
    const callId = ++latestCallId;
    
    try {
      const result = await asyncFn(...args);
      
      // Only apply if still the latest call
      if (callId === latestCallId) {
        applyResult(result);
      }
    } catch (error) {
      // Only throw if still the latest call
      if (callId === latestCallId) {
        throw error;
      }
      // Otherwise silently ignore outdated errors
    }
  };
}
